import { LazyQueryHookOptions, OperationVariables, QueryResult, useLazyQuery } from '@apollo/client'
import { DocumentNode } from 'graphql'
import merge from 'lodash/merge'
import { ChangeEvent, useCallback, useEffect, useRef } from 'react'
import { OrderDirection } from '__generatedTypes__/globalTypes'

export type Order = {
  field: string
  direction: OrderDirection
} | null

export type Filters = Record<string, unknown>

export type Params<TFilters extends Filters> = {
  page: number
  take: number
  search: string
  filters: Partial<TFilters>
  order: Order
}

export const FIRST_PAGE = 1
export const TAKE_COUNT = 10
export const MIN_TAKE_COUNT = 1
export const MAX_TAKE_COUNT = 999

export const INITIAL_PARAMS = {
  page: FIRST_PAGE,
  take: TAKE_COUNT,
  search: '',
  filters: {},
  order: null,
}

export function mergeInitialParams<TFilters extends Filters>(
  initialParams?: Partial<Params<TFilters>>
): Params<TFilters> {
  return merge({}, INITIAL_PARAMS, initialParams ?? {})
}

export function useQueryWithParams<
  TData,
  TVariables extends OperationVariables,
  TDataKey extends keyof TData,
  TItemsKey extends keyof TData[TDataKey],
  TCountKey extends keyof TData[TDataKey],
  TFilters extends Filters = Filters
>(
  query: DocumentNode,
  dataKey: TDataKey,
  itemsKey: TItemsKey,
  countKey: TCountKey,
  cast: (params: Params<TFilters>) => TVariables,
  onChange?: (params: Params<TFilters>) => void,
  options?: LazyQueryHookOptions<TData, TVariables>,
  initialParams?: Partial<Params<TFilters>>
) {
  const paramsRef = useRef<Params<TFilters>>(mergeInitialParams(initialParams))
  const params = paramsRef.current
  const [runQuery, { data, loading, error, previousData }] = useLazyQuery<TData, TVariables>(query, options)
  const count = data?.[dataKey]?.[countKey]
  const previousCount = previousData?.[dataKey]?.[countKey]

  useEffect(() => {
    if (count != undefined && params.page > FIRST_PAGE && (params.page - 1) * params.take > +count) {
      refetch({ page: FIRST_PAGE })
    }
  }, [params.take, params.page, count])

  type QueryResultOrVoid = Promise<QueryResult<TData, TVariables>> | void

  const refetch = useCallback(
    (partialParams?: Partial<Params<TFilters>>): Promise<QueryResult<TData, TVariables>> => {
      const currentParams = paramsRef.current

      if (!partialParams) {
        return runQuery({
          variables: cast(currentParams),
        })
      }

      const newParams: Params<TFilters> = {
        page: partialParams.page !== undefined ? partialParams.page : currentParams.page,
        take: partialParams.take !== undefined ? partialParams.take : currentParams.take,
        search: partialParams.search !== undefined ? partialParams.search : currentParams.search,
        filters: partialParams.filters !== undefined ? partialParams.filters : currentParams.filters,
        order: partialParams.order !== undefined ? partialParams.order : currentParams.order,
      }
      paramsRef.current = newParams
      onChange?.(newParams)

      return runQuery({
        variables: cast(newParams),
      })
    },
    [runQuery, cast, onChange]
  )

  const handlePageChange = useCallback(
    (eventOrValue: ChangeEvent<HTMLInputElement> | number, value?: number): QueryResultOrVoid => {
      if (typeof eventOrValue === 'number') {
        return refetch({ page: eventOrValue })
      }

      if (value !== undefined || !isNaN(+eventOrValue.target.value)) {
        return refetch({ page: value !== undefined ? value : +eventOrValue.target.value })
      }
    },
    [refetch]
  )

  const handleTakeChange = useCallback(
    (eventOrValue: ChangeEvent<HTMLInputElement> | number, value?: number): QueryResultOrVoid => {
      if (typeof eventOrValue === 'number') {
        return refetch({ page: FIRST_PAGE, take: eventOrValue })
      }

      if (value !== undefined || !isNaN(+eventOrValue.target.value)) {
        return refetch({ page: FIRST_PAGE, take: value !== undefined ? value : +eventOrValue.target.value })
      }
    },
    [refetch]
  )

  const handleSearchChange = useCallback(
    (eventOrValue: ChangeEvent<HTMLInputElement> | string, value?: string): QueryResultOrVoid => {
      if (typeof eventOrValue === 'string') {
        return refetch({ page: FIRST_PAGE, search: eventOrValue })
      }

      return refetch({ page: FIRST_PAGE, search: value !== undefined ? value : eventOrValue.target.value })
    },
    [refetch]
  )

  const handleOrderChange = useCallback(
    (value: Order): QueryResultOrVoid => refetch({ page: FIRST_PAGE, order: value }),
    [refetch]
  )

  const handleFilterChange = useCallback(
    (key: keyof TFilters, value?: TFilters[typeof key]): QueryResultOrVoid => {
      const filters = { ...paramsRef.current.filters }
      if (value === undefined) {
        delete filters[key]
      } else {
        filters[key] = value
      }

      return refetch({ page: FIRST_PAGE, filters })
    },
    [refetch]
  )

  const handleSetFilters = useCallback(
    (filters: TFilters): QueryResultOrVoid => refetch({ page: FIRST_PAGE, filters }),
    [refetch]
  )

  return {
    items: (data ?? previousData)?.[dataKey]?.[itemsKey] || [],
    count: count ?? previousCount ?? 0,
    loading,
    loaded: (count ?? previousCount) != undefined,
    error,
    refetch,
    pagination: {
      count: params.take ? Math.ceil(Number(count ?? previousCount ?? 0) / params.take) : 0,
      page: params.page,
      onChange: handlePageChange,
      take: params.take,
      onTakeChange: handleTakeChange,
    },
    search: {
      value: params.search,
      onChange: handleSearchChange,
    },
    order: {
      value: params.order,
      onChange: handleOrderChange,
    },
    filters: params.filters,
    setFilters: handleSetFilters,
    onFilterChange: handleFilterChange,
  }
}
